import { Screen } from '../ui/Screen'
import { useAuth } from '../lib/auth'
import { useWorkspace } from '../data/workspace'
import { updateAppSettings } from '../data/settings'
import { CATEGORY_HELP } from '../data/defaults'
import { TaxonomyEditor } from '../ui/TaxonomyEditor'
import { BackupSection } from '../ui/BackupSection'
import type { ThemePreference } from '../data/types'

/** How long a block can run before Now offers to split or end it. */
const STALE_CHOICES = [30, 45, 60, 90, 120, 180, 240].map((minutes) => ({
  value: minutes,
  label: minutes < 60 ? `${minutes}m` : `${minutes / 60}h`,
}))

/** Whole and half hours, which is what a working day is actually set in. */
const TARGET_CHOICES = [4, 5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 10].map((hours) => ({
  value: hours,
  label: `${hours}h`,
}))

const THEMES: Array<{ value: ThemePreference; label: string }> = [
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
  { value: 'system', label: 'System' },
]

export function SettingsPage() {
  const { user, signOutNow } = useAuth()
  const { uid, taxonomy, settings } = useWorkspace()

  return (
    <Screen wide scroll>
      <Section title="Account">
        <Row label="Signed in as" value={user?.email ?? '—'} />
        <Row label="UID" value={uid} mono />
        <p className="px-3 pb-3 text-[12px] leading-relaxed text-faint">
          Your data lives under this UID and nobody else can read it, including anyone else
          signed in to this same install.
        </p>
        <div className="border-t border-line px-3 py-3">
          <button
            type="button"
            onClick={() => void signOutNow()}
            className="rounded-lg border border-line-strong px-3 py-1.5 text-[14px] font-medium text-dim transition-colors duration-150 active:bg-raised active:text-text"
          >
            Sign out
          </button>
        </div>
      </Section>

      <Section title="Appearance">
        <div className="flex gap-2 p-3">
          {THEMES.map((theme) => (
            <button
              key={theme.value}
              type="button"
              onClick={() => void updateAppSettings(uid, { theme: theme.value })}
              aria-pressed={settings.theme === theme.value}
              className={`flex-1 rounded-lg border px-3 py-2 text-[14px] font-medium transition-colors duration-150 ${
                settings.theme === theme.value
                  ? 'border-line-strong bg-raised text-text'
                  : 'border-line text-dim'
              }`}
            >
              {theme.label}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Day">
        <HourRow
          label="Day starts at"
          value={settings.dayStartHour}
          onChange={(dayStartHour) => void updateAppSettings(uid, { dayStartHour })}
        />
        <ChoiceRow
          label="Nudge me after"
          value={settings.staleThresholdMinutes}
          options={STALE_CHOICES}
          onChange={(staleThresholdMinutes) =>
            void updateAppSettings(uid, { staleThresholdMinutes })
          }
        />
        <p className="px-3 pb-3 text-[12px] leading-relaxed text-faint">
          A session running past midnight belongs to the day it started. Every total, timeline and
          weekly rollup uses this boundary rather than midnight. Anything left running longer than
          the nudge gets a banner on Now offering to split or end it.
        </p>
      </Section>

      <Section title="Timeline">
        <HourRow
          label="Working day from"
          value={settings.workdayStartHour}
          onChange={(workdayStartHour) => void updateAppSettings(uid, { workdayStartHour })}
        />
        <HourRow
          label="Working day to"
          value={settings.workdayEndHour}
          onChange={(workdayEndHour) => void updateAppSettings(uid, { workdayEndHour })}
        />
        <ToggleRow
          label="Hide weekends"
          hint="Week view shows Monday to Friday"
          value={settings.hideWeekends}
          onChange={(hideWeekends) => void updateAppSettings(uid, { hideWeekends })}
        />
        <p className="px-3 pb-3 text-[12px] leading-relaxed text-faint">
          Today and Week open on this window and fill it exactly. The rest of the day is still there,
          one scroll away. It is only what you see — what a day is expected to hold is set below.
        </p>
      </Section>

      <Section title="A day's work">
        <ChoiceRow
          label="Hours a day"
          value={settings.dailyTargetHours}
          options={TARGET_CHOICES}
          onChange={(dailyTargetHours) => void updateAppSettings(uid, { dailyTargetHours })}
        />
        <p className="px-3 pb-3 text-[12px] leading-relaxed text-faint">
          What Insights compares your days against, added up rather than judged one at a time — a
          seven-hour day and a nine-hour one cancel out. Arriving early or staying late counts;
          anything on a flag marked "not counted" does not, which is how an hour of lunch comes out
          of a nine-hour day.
        </p>
      </Section>

      <Section title="Taxonomy">
        {taxonomy ? <TaxonomyEditor uid={uid} taxonomy={taxonomy} /> : null}
      </Section>

      <Section title="Backup">
        <BackupSection uid={uid} />
      </Section>

      <Section title="What the defaults mean">
        <ul className="px-3 py-2.5">
          {Object.entries(CATEGORY_HELP).map(([id, help]) => (
            <li key={id} className="flex gap-2 py-1 text-[12px] leading-relaxed">
              <span className="shrink-0 font-medium text-dim">{id}</span>
              <span className="text-faint">{help}</span>
            </li>
          ))}
        </ul>
      </Section>

    </Screen>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4">
      <h2 className="px-1 pb-2 text-[12px] font-semibold tracking-wide text-faint uppercase">
        {title}
      </h2>
      <div className="overflow-hidden rounded-xl border border-line bg-surface">{children}</div>
    </section>
  )
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-3 border-b border-line px-3 py-2.5 last:border-0">
      <span className="text-[15px] text-dim">{label}</span>
      <span
        className={`ml-auto min-w-0 truncate text-[15px] text-text ${mono ? 'font-mono text-[13px]' : ''}`}
      >
        {value}
      </span>
    </div>
  )
}

function HourRow({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (hour: number) => void
}) {
  return (
    <div className="flex items-center gap-3 border-b border-line px-3 py-2 last:border-0">
      <span className="text-[15px] text-dim">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="ml-auto rounded-md border border-line bg-bg px-2.5 py-1.5 text-[14px] tnum outline-none focus:border-line-strong"
      >
        {Array.from({ length: 24 }, (_, hour) => (
          <option key={hour} value={hour}>
            {String(hour).padStart(2, '0')}:00
          </option>
        ))}
      </select>
    </div>
  )
}

function ChoiceRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: number
  options: Array<{ value: number; label: string }>
  onChange: (value: number) => void
}) {
  return (
    <div className="flex items-center gap-3 border-b border-line px-3 py-2 last:border-0">
      <span className="text-[15px] text-dim">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="ml-auto rounded-md border border-line bg-bg px-2.5 py-1.5 text-[14px] tnum outline-none focus:border-line-strong"
      >
        {options.some((option) => option.value === value) ? null : (
          <option value={value}>{value}</option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function ToggleRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string
  hint?: string
  value: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-center gap-3 border-b border-line px-3 py-2.5 last:border-0">
      <span className="min-w-0">
        <span className="block text-[15px] text-dim">{label}</span>
        {hint ? <span className="block text-[12px] text-faint">{hint}</span> : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        onClick={() => onChange(!value)}
        className={`ml-auto flex h-6 w-10 shrink-0 items-center rounded-full border px-0.5 transition-colors duration-150 ${
          value ? 'border-accent bg-accent' : 'border-line-strong bg-raised'
        }`}
      >
        <span
          className={`size-4 rounded-full bg-bg transition-transform duration-150 ease-[var(--ease-out-quick)] ${
            value ? 'translate-x-4' : ''
          }`}
        />
      </button>
    </div>
  )
}
